/**
 * @license
 * @copyright Copyright Motili Inc., 2023 All Rights Reserved
 */

import { useNavigate } from 'react-router-dom';
import { setDocuments } from '../../../common/redux/ducks/contractorRegistrationForm';
import { useAppDispatch, useAppSelector } from '../../../common/redux/hooks';
import { uploadFileToBucket } from '../../../common/services/s3';
import FileUpload from './FileUpload';
import { MultipleFilesEntry } from './MultipleFilesUpload';
import {
    CONTRACTOR_QUALIFICATION_TYPE,
    ContractorDocuments,
    DOCUMENT_CATEGORIES,
    DOCUMENT_CATEGORY_OPTIONS,
    DOCUMENT_TYPES,
    DocFile,
    Qualification,
} from '../../../common/types';
import { TRADE_TYPE_OPTIONS } from '../../../common/constants';
import { FormFieldValues } from './FileUploadFormFields';

export default function FileUploadPage() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const storedUUID = useAppSelector(
        ({ contractorRegistration }) => contractorRegistration.UUID
    );
    const documents = useAppSelector(
        ({ contractorRegistration }) =>
            contractorRegistration.contractor.documents
    );

    const handleBackClick = () => navigate(-1);
    const determineCategory = (fileName: string) => {
        switch (fileName) {
            case DOCUMENT_TYPES.epaCertification:
                return DOCUMENT_CATEGORY_OPTIONS[DOCUMENT_CATEGORIES.EPA]
                    .display;
            case DOCUMENT_TYPES.secretaryOfStateRegistration:
                return DOCUMENT_CATEGORY_OPTIONS[
                    DOCUMENT_CATEGORIES.SECRETARY_OF_STATE_REGISTRATION
                ].display;
            case DOCUMENT_TYPES.w9:
                return DOCUMENT_CATEGORY_OPTIONS[DOCUMENT_CATEGORIES.W9]
                    .display;
            case DOCUMENT_TYPES.certificateOfLiability:
                return DOCUMENT_CATEGORY_OPTIONS[
                    DOCUMENT_CATEGORIES.PROOF_OF_INSURANCE
                ].display;
            case DOCUMENT_TYPES.tradeLicenses:
                return DOCUMENT_CATEGORY_OPTIONS[
                    DOCUMENT_CATEGORIES.QUALIFICATIONS
                ].display;
            case DOCUMENT_TYPES.taxSalesCertificate:
                return DOCUMENT_CATEGORY_OPTIONS[
                    DOCUMENT_CATEGORIES.TAX_SALES_CERTIFICATE
                ].display;
            default:
                return '';
        }
    };

    const handleFileUpload = async (
        fileName: string,
        file: File,
        formFieldValues: FormFieldValues
    ) => {
        try {
            const { storedFileUrl, key, bucket, source } =
                await uploadFileToBucket(
                    'contractor',
                    storedUUID,
                    fileName,
                    file
                );
            dispatch(
                setDocuments({
                    ...documents,
                    [fileName]: [
                        {
                            name: file.name,
                            category: determineCategory(fileName),
                            resourceType: file.type,
                            key,
                            url: storedFileUrl,
                            secureUrl: '',
                            timeStamp: new Date().toISOString(),
                            validFrom:
                                formFieldValues.effectiveDate ??
                                formFieldValues.issueDate ??
                                null,
                            validTo: formFieldValues.expirationDate ?? null,
                            taxIdNumber: formFieldValues.taxIdNumber ?? null,
                            taxIdType: formFieldValues.taxIdType ?? null,
                            bucket,
                            source,
                        },
                    ],
                })
            );
        } catch (error) {
            console.error(error);
        }
    };

    const handleMultipleFilesUpload = async (
        fileName: string,
        entries: MultipleFilesEntry[] = []
    ) => {
        try {
            const uploadPromises = entries.map(
                async ({ file, formFieldValues }) => {
                    const { storedFileUrl, key, bucket, source } =
                        await uploadFileToBucket(
                            'contractor',
                            storedUUID,
                            fileName,
                            file
                        );
                    const docFile: DocFile = {
                        name: file.name,
                        category: determineCategory(fileName),
                        resourceType: file.type,
                        key,
                        url: storedFileUrl,
                        secureUrl: '',
                        timeStamp: new Date().toISOString(),
                        validFrom:
                            formFieldValues.effectiveDate ??
                            formFieldValues.issueDate ??
                            null,
                        validTo: formFieldValues.expirationDate ?? null,
                        bucket,
                        source,
                    };

                    if (fileName === DOCUMENT_TYPES.tradeLicenses) {
                        const tradeOption = TRADE_TYPE_OPTIONS.find(
                            option => option.value === formFieldValues.tradeType
                        );
                        const qualification: Qualification = {
                            type: CONTRACTOR_QUALIFICATION_TYPE.LICENSE,
                            fileName: file.name,
                            tradeTypeId: tradeOption?.tradeTypeId ?? '',
                            tradeSubTypeId: tradeOption?.tradeSubTypeId ?? '',
                            file: docFile,
                        };
                        return qualification;
                    }

                    return {
                        ...docFile,
                        taxIdNumber: formFieldValues.taxIdNumber ?? null,
                        taxIdType: formFieldValues.taxIdType ?? null,
                        state: formFieldValues.state ?? null,
                    };
                }
            );
            const uploadedFiles = await Promise.all(uploadPromises);
            dispatch(
                setDocuments({
                    ...documents,
                    [fileName]: uploadedFiles,
                } as ContractorDocuments)
            );
            return uploadedFiles;
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <FileUpload
            handleBackClick={handleBackClick}
            handleFileUpload={handleFileUpload}
            handleMultipleFilesUpload={handleMultipleFilesUpload}
        />
    );
}
